import React, { useCallback, useState } from "react";
import { Button, message } from "antd";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { LandTourDetail } from "../../../../interfaces/land-tour.interface";

const GTRIP_LOGO = "/iceholidays-assets/images/gtrip_logo.png";

interface Props {
  landTour: LandTourDetail;
  buttonText?: string;
}

// Ported from the agent portal's DownloadItineraryPDF
// (iceholidays app/javascript/src/components/common/DownloadItineraryPDF.js) for issue #698
// ("can refer B2B code so do together #629") - no backend endpoint exists or is needed, the
// agent portal builds the same PDF entirely client-side from the tour's own show payload.
//
// Adapted from a class component (whose instance carried mutable layout state such as
// `this.currentY` across its helper methods) to this codebase's function-component
// convention: `ctx` plays the same role a class instance did, threaded through the helper
// functions below instead of closing over `this`. The PDF-generation algorithm itself -
// canvas slicing at blank rows, section layout, the bilingual disclaimer - is unchanged.
//
// Field access is adapted to LandTourDetail's already-mapped, camelCased shape rather than
// the raw jbuilder response the original reads directly (e.g. `pricingCategories[].price_dates`
// here is already one flat entry per bookable date window - see LandToursApi.mapPricingCategories -
// where the original walks a raw, one-level-deeper `price_dates[].dates[]`).
interface PdfContext {
  pdf: jsPDF;
  pageWidth: number;
  pageHeight: number;
  margin: number;
  contentWidth: number;
  contentBottomLimit: number;
  currentY: number;
}

const FONT_FAMILY = "font-family: 'Century Gothic', Arial, sans-serif;";
const FONT_SIZE_10 = "font-size: 10px;";
const FONT_SIZE_11 = "font-size: 11px;";
const TEXT_COLOR_PRIMARY = "color: #2c3e50;";
const TEXT_COLOR_SECONDARY = "color: #555;";
const TEXT_COLOR_WHITE = "color: #ffffff;";
const BG_COLOR_HEADER = "background-color: #44b3e1;";
const BG_COLOR_LIGHT = "background-color: #e8e8e8;";
const LABEL_STYLE = `${TEXT_COLOR_PRIMARY} margin-bottom: 2px; ${FONT_SIZE_11} ${FONT_FAMILY}`;
const TEXT_BODY_STYLE = `${TEXT_COLOR_SECONDARY} ${FONT_SIZE_11} ${FONT_FAMILY}`;
const TABLE_STYLE = `width: 100%; border-collapse: collapse; ${FONT_SIZE_10} ${FONT_FAMILY}`;
const TABLE_HEADER_STYLE = `border: 1px solid #ddd; padding: 4px; text-align: center; font-weight: bold; ${BG_COLOR_LIGHT}`;
const SECTION_HEADER_STYLE = `${TEXT_COLOR_WHITE} ${BG_COLOR_HEADER} padding: 3px 8px 5px; margin-bottom: 8px; ${FONT_SIZE_11} line-height: 1.2; font-weight: bold; ${FONT_FAMILY}`;

const DISCLAIMER_HTML = `
  <div style="margin-top: 0px; padding-top: 8px; padding-bottom: 4px; border-top: 1px solid #ddd;">
    <div style="margin-bottom: 3px;">
      <h4 style="${TEXT_COLOR_PRIMARY} margin-bottom: 2px; font-weight: bold; ${FONT_SIZE_11} ${FONT_FAMILY}">Disclaimer</h4>
      <p style="${TEXT_COLOR_PRIMARY} margin-bottom: 1px; line-height: 1.3; ${FONT_SIZE_11} ${FONT_FAMILY}">
        Sequences of itinerary are subject to local arrangement. Itineraries, meals, hotels and transportation are subject to change without prior notice.
      </p>
      <p style="${TEXT_COLOR_PRIMARY} margin-bottom: 1px; line-height: 1.3; ${FONT_SIZE_11} ${FONT_FAMILY}">
        All pictures are for illustration purpose only.
      </p>
    </div>
    <div style="margin-top: 4px;">
      <h4 style="${TEXT_COLOR_PRIMARY} margin-bottom: 2px; font-weight: bold; ${FONT_SIZE_11} ${FONT_FAMILY}">免责声明</h4>
      <p style="${TEXT_COLOR_PRIMARY} margin-bottom: 1px; line-height: 1.3; ${FONT_SIZE_11} ${FONT_FAMILY}">
        行程次序以当地旅行社安排为准。行程，膳食，住宿，交通等可能会因不可抗拒因素而更动，恕不另行通知。
      </p>
      <p style="${TEXT_COLOR_PRIMARY} margin-bottom: 0; line-height: 1.3; ${FONT_SIZE_11} ${FONT_FAMILY}">
        图片仅供参考。
      </p>
    </div>
  </div>
`;

function createOffscreenContainer(htmlContent: string, width = "600px", isTable = false): HTMLDivElement {
  const div = document.createElement("div");
  div.style.position = "absolute";
  div.style.left = "-9999px";
  div.style.top = "-9999px";
  div.style.width = width;
  // Extra padding for tables to prevent edge clipping.
  div.style.padding = isTable ? "12px" : "8px";
  div.style.fontFamily = "Century Gothic, Arial, sans-serif";
  div.style.boxSizing = "border-box";
  div.style.overflow = "visible";
  div.innerHTML = htmlContent;
  return div;
}

// True if a horizontal row of pixels in the canvas is blank (all white/near-white) - used to
// find safe page-break positions that don't cut through a line of text.
function isCanvasRowBlank(imageData: ImageData, canvasWidth: number, row: number, threshold = 245): boolean {
  const rowStart = row * canvasWidth * 4; // RGBA
  for (let x = 0; x < canvasWidth; x++) {
    const idx = rowStart + x * 4;
    if (imageData.data[idx] < threshold || imageData.data[idx + 1] < threshold || imageData.data[idx + 2] < threshold) {
      return false;
    }
  }
  return true;
}

// The highest blank row at or before `idealRow`, searching up to `searchWindow` rows upward.
// Returns idealRow (a hard cut) if no blank row is found.
function findSafeBreakRow(imageData: ImageData, canvasWidth: number, idealRow: number, searchWindow = 40): number {
  const start = Math.max(0, idealRow - searchWindow);
  for (let row = idealRow; row >= start; row--) {
    if (isCanvasRowBlank(imageData, canvasWidth, row)) return row;
  }
  return idealRow;
}

async function addNewPage(ctx: PdfContext): Promise<void> {
  try {
    ctx.pdf.addPage();
    ctx.currentY = ctx.margin;
  } catch (error) {
    console.error("Failed to add new page:", error);
  }
}

async function addContentToPage(ctx: PdfContext, contentHeight: number): Promise<void> {
  if (ctx.currentY + contentHeight > ctx.contentBottomLimit) {
    await addNewPage(ctx);
  }
}

// Adds a canvas to the PDF, slicing it across pages as needed and snapping each cut to the
// nearest blank row so a page break never bisects a line of text.
async function addCanvasToPdf(ctx: PdfContext, canvas: HTMLCanvasElement): Promise<void> {
  const pixelsPerMm = canvas.width / ctx.contentWidth;
  let sourceOffsetY = 0;

  const canvasCtx = canvas.getContext("2d");
  let imageData: ImageData | null = null;
  try {
    imageData = canvasCtx?.getImageData(0, 0, canvas.width, canvas.height) ?? null;
  } catch (error) {
    // Cross-origin canvas - pixel scanning unavailable, fall back to hard cuts.
    console.warn("addCanvasToPdf: could not read pixel data, page breaks may clip text", error);
  }

  while (sourceOffsetY < canvas.height) {
    let availableHeight = ctx.contentBottomLimit - ctx.currentY;

    if (availableHeight <= 0.1) {
      await addNewPage(ctx);
      availableHeight = ctx.contentBottomLimit - ctx.currentY;
    }

    const remainingPixels = canvas.height - sourceOffsetY;
    const idealSlicePixels = Math.min(remainingPixels, Math.max(1, Math.floor(availableHeight * pixelsPerMm)));

    let sliceHeightPixels = idealSlicePixels;
    if (imageData && idealSlicePixels < remainingPixels) {
      const idealRow = sourceOffsetY + idealSlicePixels;
      const safeRow = findSafeBreakRow(imageData, canvas.width, idealRow);
      sliceHeightPixels = Math.max(1, safeRow - sourceOffsetY);
    }

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPixels;
    const sliceCtx = sliceCanvas.getContext("2d");
    if (!sliceCtx) throw new Error("Failed to create canvas context for PDF rendering");

    sliceCtx.drawImage(canvas, 0, sourceOffsetY, canvas.width, sliceHeightPixels, 0, 0, sliceCanvas.width, sliceCanvas.height);

    const renderedHeight = sliceHeightPixels / pixelsPerMm;

    try {
      ctx.pdf.addImage(sliceCanvas.toDataURL("image/png"), "PNG", ctx.margin, ctx.currentY, ctx.contentWidth, renderedHeight);
    } catch (error) {
      console.error("Failed to add canvas slice to PDF:", error);
    }

    sourceOffsetY += sliceHeightPixels;
    ctx.currentY += renderedHeight;

    if (sourceOffsetY < canvas.height) await addNewPage(ctx);
  }
}

async function renderElementToCanvas(element: HTMLElement, width?: number): Promise<HTMLCanvasElement> {
  document.body.appendChild(element);
  // Wait for the browser to paint so height reflects the full content.
  await new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 0);
  });
  const captureWidth = width || element.scrollWidth || element.offsetWidth || 600;

  try {
    return await html2canvas(element, { width: captureWidth, scale: 2, useCORS: true, allowTaint: true });
  } finally {
    document.body.removeChild(element);
  }
}

// Reusable for the Price Table, Compulsory Charges, Surcharge Details, and Remarks sections.
async function addHtmlSectionToPdf(ctx: PdfContext, htmlContent: string, sectionName: string): Promise<void> {
  const isTable = sectionName.includes("Table") || sectionName.includes("Charges");
  const div = createOffscreenContainer(htmlContent, "600px", isTable);
  document.body.appendChild(div);

  try {
    const actualHeight = Math.max(div.scrollHeight, div.offsetHeight);

    const canvas = await html2canvas(div, {
      width: 600,
      height: actualHeight,
      windowWidth: 600,
      windowHeight: actualHeight,
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: "#ffffff",
      onclone: (clonedDocument) => {
        const clonedDiv = clonedDocument.body.firstChild as HTMLElement | null;
        if (clonedDiv) {
          clonedDiv.style.width = "600px";
          clonedDiv.style.minHeight = `${actualHeight}px`;
          clonedDiv.style.height = `${actualHeight}px`;
          clonedDiv.style.overflow = "visible";
        }
      },
    });

    await addCanvasToPdf(ctx, canvas);
    ctx.currentY += 3;
  } catch (error) {
    console.error(`Failed to add ${sectionName} section image:`, error);
  } finally {
    document.body.removeChild(div);
  }
}

async function addLastUpdatedDate(ctx: PdfContext, updatedAt: string | undefined): Promise<void> {
  const formattedDate = updatedAt
    ? new Date(updatedAt).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })
    : "";

  const dateDiv = createOffscreenContainer("", "400px");
  dateDiv.style.padding = "0";
  dateDiv.innerHTML = `
    <div style="color: #999; font-size: 10px; font-style: italic; text-align: right; font-family: 'Century Gothic', Arial, sans-serif; white-space: nowrap;">
      Last updated: ${formattedDate}
    </div>
  `;
  document.body.appendChild(dateDiv);

  try {
    const dateCanvas = await html2canvas(dateDiv, { width: 400, height: dateDiv.scrollHeight, scale: 2, useCORS: true, allowTaint: true });
    const dateHeight = (dateCanvas.height * 120) / dateCanvas.width;
    const dateWidth = 120;
    const dateX = ctx.pageWidth - ctx.margin - dateWidth;
    const dateY = Math.max(ctx.pageHeight - ctx.margin - dateHeight, ctx.margin);
    ctx.pdf.addImage(dateCanvas.toDataURL("image/png"), "PNG", dateX, dateY, dateWidth, dateHeight);
  } catch (error) {
    console.error("Failed to create date canvas:", error);
  } finally {
    document.body.removeChild(dateDiv);
  }
}

function loadCenturyGothicFont(): Promise<void> {
  return new Promise((resolve) => {
    if (document.fonts?.check('1em "Century Gothic"')) {
      resolve();
      return;
    }

    const link = document.createElement("link");
    link.href = "https://fonts.cdnfonts.com/css/century-gothic";
    link.rel = "stylesheet";
    link.onload = () => setTimeout(resolve, 100);
    link.onerror = () => {
      console.warn("Century Gothic font failed to load, using fallback fonts");
      resolve();
    };
    document.head.appendChild(link);
  });
}

function buildPriceTableHtml(landTour: LandTourDetail): string {
  let html = `
    <div style="margin-top: 12px; padding-top: 8px;">
      <h3 style="${SECTION_HEADER_STYLE}">Price Table</h3>
      <table style="${TABLE_STYLE}">
        <thead>
          <tr style="${BG_COLOR_LIGHT}">
            <th style="${TABLE_HEADER_STYLE} text-align: left; width: 35%;">Category</th>
            <th style="${TABLE_HEADER_STYLE} width: 35%;">${landTour.incFlight ? "Departure Date" : "Travelling Period"}</th>
            <th style="${TABLE_HEADER_STYLE} width: 15%;">Prices (Per Pax)</th>
            <th style="${TABLE_HEADER_STYLE} width: 15%;">Single Supplement</th>
          </tr>
        </thead>
        <tbody>
  `;

  landTour.pricingCategories.forEach((category) => {
    const windows = [...category.price_dates].sort((a, b) => a.date[0]?.localeCompare(b.date[0] ?? "") ?? 0);

    windows.forEach((window, index) => {
      const dateStr = window.fullDate || window.date.join(", ");
      const priceStr = window.quantities
        .map((tier) => `<strong>${tier.quantity_str.replace("..", "-")} pax:</strong> RM ${tier.adult}`)
        .join("<br/>");

      const categoryCell =
        index === 0
          ? `<td style="border: 1px solid #ddd; padding: 4px; text-align: left;" rowspan="${windows.length}">
              <div style="font-weight: bold;">${category.category}</div>
              ${category.description ? `<div style="font-size: 8px; color: #666; margin-top: 2px;">${category.description}</div>` : ""}
            </td>`
          : "";

      const singleSuppStr = window.singleSupplementPrice > 0 ? `RM ${window.singleSupplementPrice}` : "-";

      html += `
        <tr>
          ${categoryCell}
          <td style="border: 1px solid #ddd; padding: 4px; text-align: center; font-size: 9px;">${dateStr}</td>
          <td style="border: 1px solid #ddd; padding: 4px; text-align: left; font-size: 9px;">${priceStr}</td>
          <td style="border: 1px solid #ddd; padding: 4px; text-align: center; font-size: 9px;">${singleSuppStr}</td>
        </tr>
      `;
    });
  });

  html += `</tbody></table></div>`;

  if (landTour.tipping?.trim()) {
    html += `
      <div style="margin-top: 8px; display: flex; flex-direction: column; align-items: flex-start;">
        <strong style="${LABEL_STYLE}">Tipping:</strong>
        <div style="${TEXT_BODY_STYLE}">${landTour.tipping.replace(/\n/g, "<br/>")}</div>
      </div>
    `;
  }

  return html;
}

function buildCompulsoryChargesHtml(landTour: LandTourDetail): string {
  let html = `
    <div style="margin-top: 12px; padding-top: 8px;">
      <h3 style="${SECTION_HEADER_STYLE}">Compulsory Charges</h3>
      <div style="margin-top: 8px; display: flex; flex-direction: column; gap: 8px;">
  `;

  (landTour.compulsoryCharges || []).forEach((charge) => {
    const typeLabel = charge.type ? ` (${charge.type})` : "";
    html += `
      <div style="display: flex; flex-direction: column; align-items: flex-start; padding: 6px 0;">
        <strong style="${LABEL_STYLE}">${charge.title || "Charge"}:</strong>
        <div style="${TEXT_BODY_STYLE}">RM ${charge.price || 0}${typeLabel}</div>
      </div>
    `;
  });

  html += `</div></div>`;
  return html;
}

function buildSurchargeTableHtml(landTour: LandTourDetail): string {
  let html = `
    <div style="margin-top: 12px; padding-top: 8px;">
      <h3 style="${SECTION_HEADER_STYLE}">Surcharge Details</h3>
      <table style="width: 100%; border-collapse: collapse; font-size: 10px; font-family: 'Century Gothic', Arial, sans-serif;">
        <thead>
          <tr style="background-color: #e8e8e8;">
            <th style="border: 1px solid #ddd; padding: 4px; text-align: left; font-weight: bold; width: 25%;">Title</th>
            <th style="border: 1px solid #ddd; padding: 4px; text-align: center; font-weight: bold; width: 15%;">Type</th>
            <th style="border: 1px solid #ddd; padding: 4px; text-align: center; font-weight: bold; width: 20%;">Period</th>
            <th style="border: 1px solid #ddd; padding: 4px; text-align: center; font-weight: bold; width: 20%;">Prices</th>
            <th style="border: 1px solid #ddd; padding: 4px; text-align: center; font-weight: bold; width: 20%;">Categories</th>
          </tr>
        </thead>
        <tbody>
  `;

  landTour.surcharges.forEach((surcharge) => {
    let categoriesList = "-";
    if (surcharge.categories?.length) {
      const bulletItems = surcharge.categories
        .map(
          (category) =>
            `<div style="display: flex; margin-bottom: 4px; line-height: 1.5;"><span style="min-width: 8px; flex-shrink: 0;">•</span><span style="margin-left: 2px; flex: 1;">${category.name}</span></div>`
        )
        .join("");
      categoriesList = `<div style="font-size: 8px;">${bulletItems}</div>`;
    }

    let period = "-";
    if (surcharge.period_type === "fixed" && surcharge.dates?.length) {
      period = surcharge.dates
        .map((date) => new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }))
        .join(", ");
    } else if (surcharge.period_type === "date_range" && surcharge.period) {
      const start = new Date(surcharge.period.start_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
      const end = new Date(surcharge.period.end_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
      period = `${start} to ${end}`;
    }

    let prices = "-";
    if (surcharge.prices?.length) {
      prices = surcharge.prices
        .map((price) => {
          const category = price.category ? price.category.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "N/A";
          return price.price_type === "fixed"
            ? `${category}: RM ${parseFloat(String(price.amount_price || 0))}`
            : `${category}: ${parseFloat(String(price.percentage || 0))}%`;
        })
        .join('<br style="line-height: 1.5;" />');
    }

    html += `
      <tr>
        <td style="border: 1px solid #ddd; padding: 4px; text-align: left; font-size: 9px; vertical-align: top;">${surcharge.title || "-"}</td>
        <td style="border: 1px solid #ddd; padding: 4px; text-align: center; font-size: 8px; vertical-align: top;">${surcharge.surcharge_type_title || surcharge.surcharge_type || "-"}</td>
        <td style="border: 1px solid #ddd; padding: 4px; text-align: center; font-size: 8px; vertical-align: top;">${period}</td>
        <td style="border: 1px solid #ddd; padding: 4px; text-align: left; font-size: 8px; vertical-align: top; line-height: 1.5;">${prices}</td>
        <td style="border: 1px solid #ddd; padding: 4px; text-align: left; font-size: 8px; vertical-align: top;">${categoriesList}</td>
      </tr>
    `;
  });

  html += `</tbody></table></div>`;
  return html;
}

function buildRemarksHtml(termsAndConditions: string): string {
  const body = termsAndConditions
    .split("\n")
    .map((line) => {
      const trimmed = line.trim();
      return trimmed === ""
        ? '<div style="height: 6px;"></div>'
        : `<p style="margin: 0 0 2px 0; line-height: 1.5; ${TEXT_COLOR_PRIMARY} ${FONT_SIZE_10} ${FONT_FAMILY}">${trimmed}</p>`;
    })
    .join("");

  return `
    <div style="margin-top: 12px; padding-top: 8px;">
      <h3 style="${SECTION_HEADER_STYLE}">Remarks</h3>
      <div style="padding: 4px 0 4px 3px;">${body}</div>
    </div>
  `;
}

async function addImageGallery(ctx: PdfContext, images: string[]): Promise<void> {
  const imagesToShow = images.slice(0, 4);
  const maxHeight = 60;
  let spacing = 5;
  const totalSpacing = 3 * spacing;
  let imageWidth = (ctx.contentWidth - totalSpacing) / 4;

  const dimensions = await Promise.all(
    imagesToShow.map(
      (image) =>
        new Promise<{ width: number; height: number; originalWidth: number; originalHeight: number }>((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const aspectRatio = img.width / img.height;
            resolve({ width: imageWidth, height: imageWidth / aspectRatio, originalWidth: img.width, originalHeight: img.height });
          };
          img.onerror = () => resolve({ width: imageWidth, height: maxHeight, originalWidth: 1, originalHeight: 1 });
          img.src = image;
        })
    )
  );

  const maxRowHeight = Math.max(...dimensions.map((d) => d.height));
  await addContentToPage(ctx, maxRowHeight + 8);

  const gridStartX = ctx.margin;
  const rightmostImageX = gridStartX + 3 * (imageWidth + spacing) + imageWidth;
  if (rightmostImageX > ctx.pageWidth - ctx.margin) {
    const maxAvailableWidth = ctx.pageWidth - 2 * ctx.margin;
    imageWidth = (maxAvailableWidth - totalSpacing) / 4;
    spacing = (maxAvailableWidth - 4 * imageWidth) / 3;
    dimensions.forEach((dim, index) => {
      const aspectRatio = dim.originalWidth / dim.originalHeight;
      dimensions[index] = { ...dim, width: imageWidth, height: Math.min(imageWidth / aspectRatio, maxHeight) };
    });
  }

  imagesToShow.forEach((image, index) => {
    const dim = dimensions[index];
    const currentX = gridStartX + index * (imageWidth + spacing);
    if (currentX + dim.width > ctx.pageWidth - ctx.margin) {
      console.warn(`Image ${index} would exceed page bounds, skipping`);
      return;
    }

    try {
      ctx.pdf.addImage(image, "JPEG", currentX, ctx.currentY, dim.width, dim.height, undefined, "FAST");
    } catch (error) {
      console.warn(`Failed to add image ${index}:`, error);
      ctx.pdf.setFillColor(0, 128, 128);
      ctx.pdf.rect(currentX, ctx.currentY, dim.width, dim.height, "F");
      ctx.pdf.setTextColor(255, 255, 255);
      ctx.pdf.setFontSize(8);
      ctx.pdf.text("Image", currentX + dim.width / 2 - 10, ctx.currentY + dim.height / 2 + 3);
    }
  });

  ctx.currentY += maxRowHeight + 3;
}

async function generateItineraryPdf(landTour: LandTourDetail): Promise<void> {
  await loadCenturyGothicFont();

  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 5;
  const footerReserve = 10;
  const contentWidth = pageWidth - 2 * margin;

  const ctx: PdfContext = {
    pdf,
    pageWidth,
    pageHeight,
    margin,
    contentWidth,
    contentBottomLimit: pageHeight - margin - footerReserve,
    currentY: 0,
  };

  // Header: title (+ Chinese title) and the G-trip logo.
  const headerDiv = createOffscreenContainer("");
  headerDiv.style.padding = "0 10px";
  const title = landTour.title || "Tour Title";
  headerDiv.innerHTML = `
    <div style="display: flex; flex-direction: column; width: 100%; gap: 10px; margin-top: 6px; padding-bottom: 16px; box-sizing: border-box;">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; width: 100%; gap: 20px;">
        <div style="flex: 1 1 auto; max-width: 400px; display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-start; gap: 4px;">
          <h1 style="color: #2c3e50; margin: 0; font-size: 18px; font-weight: 700; font-family: 'Century Gothic', Arial, sans-serif; line-height: 1.3; word-wrap: break-word; overflow-wrap: break-word; width: 100%;">${title}</h1>
          ${landTour.cnTitle ? `<h2 style="color: #2c3e50; margin: 0; font-size: 18px; font-weight: 700; font-family: 'Century Gothic', Arial, sans-serif; line-height: 1.3; word-wrap: break-word; overflow-wrap: break-word; width: 100%;">${landTour.cnTitle}</h2>` : ""}
        </div>
        <div style="flex: 0 0 auto; display: flex; align-items: flex-start;">
          <img src="${GTRIP_LOGO}" alt="G-trip Logo" style="width: auto; height: 106px; object-fit: contain; margin-right: -20px;">
        </div>
      </div>
    </div>
  `;
  const headerCanvas = await renderElementToCanvas(headerDiv);
  const headerHeight = (headerCanvas.height * contentWidth) / headerCanvas.width;
  await addContentToPage(ctx, headerHeight);
  pdf.addImage(headerCanvas.toDataURL("image/png"), "PNG", margin, ctx.currentY, contentWidth, headerHeight);
  ctx.currentY += headerHeight + 3;

  if (landTour.code?.trim()) {
    const codeDiv = createOffscreenContainer("", "600px");
    codeDiv.style.padding = "0 10px 6px";
    codeDiv.innerHTML = `
      <div style="color: #666; font-size: 12px; font-family: 'Century Gothic', Arial, sans-serif; width: 100%;">
        <p style="margin: 0; font-weight: 500; color: #2c3e50; line-height: 1.4; word-break: break-word; overflow-wrap: anywhere; display: flex; flex-wrap: wrap; gap: 6px;">
          <strong style="color: #2c3e50;">Tour Code:</strong>
          <span style="color: #2c3e50; font-weight: 600; flex: 1 1 auto;">${landTour.code}</span>
        </p>
      </div>
    `;
    const codeCanvas = await renderElementToCanvas(codeDiv);
    const codeHeight = (codeCanvas.height * contentWidth) / codeCanvas.width;
    await addContentToPage(ctx, codeHeight);
    pdf.addImage(codeCanvas.toDataURL("image/png"), "PNG", margin, ctx.currentY, contentWidth, codeHeight);
    ctx.currentY += codeHeight + 3;
  }

  await addLastUpdatedDate(ctx, landTour.landTourUpdatedAt);

  // Day-by-day itinerary.
  for (const day of landTour.itinerary) {
    const dayDiv = createOffscreenContainer("");
    dayDiv.style.padding = "8px 8px 0 8px";
    dayDiv.innerHTML = `
      <div style="margin-bottom: 0px;">
        <h3 style="color: #ffffff; background-color: #44b3e1; padding: 3px 8px 5px; margin-bottom: 8px; font-size: 11px; line-height: 1.2; display: flex; align-items: center; font-weight: bold; font-family: 'Century Gothic', Arial, sans-serif;">
          ${day.day_no}: ${day.title}
        </h3>
        <div style="margin-bottom: 12px; line-height: 1.3; font-size: 11px; font-family: 'Century Gothic', Arial, sans-serif;">
          ${(day.description || "").replace(/\n/g, "<br/>")}
        </div>
        <div style="margin-top: 6px;">
          <div style="display: flex; align-items: flex-start; gap: 40px;">
            <div style="display: flex; flex-direction: column; align-items: flex-start; flex: 0.6;">
              <strong style="color: #2c3e50; margin-bottom: 2px; font-size: 11px; font-family: 'Century Gothic', Arial, sans-serif;">Hotel In:</strong>
              <div style="color: #555; font-size: 11px; font-family: 'Century Gothic', Arial, sans-serif;">
                ${day.cities?.length ? day.cities.map((c) => c.city).join("<br/>") : "-"}
              </div>
            </div>
            <div style="display: flex; flex-direction: column; align-items: flex-start; flex: 1;">
              <strong style="color: #2c3e50; margin-bottom: 2px; font-size: 11px; font-family: 'Century Gothic', Arial, sans-serif;">Meal(s):</strong>
              <span style="color: #555; font-size: 11px; font-family: 'Century Gothic', Arial, sans-serif;">${day.inclusion || "-"}</span>
            </div>
          </div>
        </div>
      </div>
    `;
    const dayCanvas = await renderElementToCanvas(dayDiv);
    await addCanvasToPdf(ctx, dayCanvas);
    ctx.currentY += 3;
  }

  // Mid-document disclaimer.
  const disclaimerCanvas = await renderElementToCanvas(createOffscreenContainer(DISCLAIMER_HTML, "600px"));
  const disclaimerHeight = (disclaimerCanvas.height * contentWidth) / disclaimerCanvas.width;
  await addContentToPage(ctx, disclaimerHeight);
  pdf.addImage(disclaimerCanvas.toDataURL("image/png"), "PNG", margin, ctx.currentY, contentWidth, disclaimerHeight);
  ctx.currentY += disclaimerHeight + 3;

  if (landTour.pricingCategories.length) {
    await addNewPage(ctx);
    await addHtmlSectionToPdf(ctx, buildPriceTableHtml(landTour), "Price Table");
  }

  if (landTour.compulsoryCharges?.length) {
    await addHtmlSectionToPdf(ctx, buildCompulsoryChargesHtml(landTour), "Compulsory Charges");
  }

  if (landTour.surcharges.length) {
    await addNewPage(ctx);
    await addHtmlSectionToPdf(ctx, buildSurchargeTableHtml(landTour), "Surcharge Details");
  }

  if (landTour.termsAndConditions?.trim()) {
    await addHtmlSectionToPdf(ctx, buildRemarksHtml(landTour.termsAndConditions), "Remarks");
  }

  if (landTour.images.length) {
    await addImageGallery(ctx, landTour.images);
  }

  await addContentToPage(ctx, 60); // Reserve space for the closing disclaimer.
  const finalDisclaimerCanvas = await renderElementToCanvas(createOffscreenContainer(DISCLAIMER_HTML));
  await addCanvasToPdf(ctx, finalDisclaimerCanvas);
  ctx.currentY += 3;

  const totalPages = pdf.internal.getNumberOfPages();
  for (let page = 1; page <= totalPages; page++) {
    pdf.setPage(page);
    await addLastUpdatedDate(ctx, landTour.landTourUpdatedAt);
  }

  pdf.save(`itinerary_${landTour.code || landTour.id || "tour"}.pdf`);
}

export default function DownloadItineraryButton({ landTour, buttonText }: Props) {
  const [loading, setLoading] = useState(false);

  const handleDownload = useCallback(async () => {
    setLoading(true);
    try {
      await generateItineraryPdf(landTour);
      message.success("Itinerary PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating itinerary PDF:", error);
      message.error("Failed to generate PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [landTour]);

  return (
    <Button
      className="ground-tour-show-v2__download-btn"
      onClick={handleDownload}
      loading={loading}
      disabled={loading}
    >
      {!loading && <img src="/iceholidays-assets/images/icons/icon-download.png" alt="" aria-hidden="true" />}
      {loading ? "Generating PDF..." : buttonText || "Download Itinerary"}
    </Button>
  );
}
